
import React from "react";
import { Offer } from "@/types/offer";
import { SessionCalendar } from "./SessionCalendar";
import { SessionsTable } from "./sessions-table/SessionsTable";
import { SortableOption } from "@/types/fieldsOptions";
import type { CreateColumnsOptions } from "./sessions-table/columns";
import type { Variation } from "../../types/variation";

interface SessionsContentProps {
  viewMode: 'calendar' | 'table';
  isLoading: boolean;
  offersLength: number;
  filteredCalendarEvents: any[];
  filteredTableOffers: Offer[];
  variations: Variation[];
  calendarProgramFilter?: string;
  tableDateFilter?: 'future' | 'past' | 'all' | 'placeholder';
  onAddSession: () => void;
  onEditSession: (event: any) => void;
  /** Omit in org calendar mode so day cells do not open the add-session flow. */
  onDayClick?: (date: Date) => void;
  onFilterChange: (variationKey: string | undefined) => void;
  /** Org calendar: controlled month for two-month program bucketing. */
  calendarBaseMonth?: Date;
  onCalendarBaseMonthChange?: (date: Date) => void;
  onEditOffer: (offer: Offer) => void;
  onDeleteOffer: (offerId: string) => void;
  onCopyOffer?: (offerId: string) => void;
  onBulkUpdateOffers?: (offerIds: string[], updateData: Partial<Offer>, suppressToasts?: boolean) => Promise<void>;
  onDateFilterChange?: (value: 'future' | 'past' | 'all' | 'placeholder') => void;
  availabilityStatusOptions?: SortableOption[];
  signupStatusOptions?: SortableOption[];
  onRefreshOffers?: () => Promise<void>;
  isAdmin?: boolean;
  /** Optional table column config (e.g. Location column for org-level sessions). */
  tableColumnOptions?: CreateColumnsOptions;
}

export const SessionsContent: React.FC<SessionsContentProps> = ({
  viewMode,
  isLoading,
  offersLength,
  filteredCalendarEvents,
  filteredTableOffers,
  variations,
  calendarProgramFilter,
  tableDateFilter,
  onAddSession,
  onEditSession,
  onDayClick,
  onFilterChange,
  onEditOffer,
  onDeleteOffer,
  onCopyOffer,
  onBulkUpdateOffers,
  onDateFilterChange,
  availabilityStatusOptions = [],
  signupStatusOptions = [],
  onRefreshOffers,
  isAdmin,
  tableColumnOptions,
  calendarBaseMonth,
  onCalendarBaseMonthChange,
}) => {
  if (viewMode === 'calendar') {
    return (
      <SessionCalendar 
        events={filteredCalendarEvents}
        isLoading={isLoading && offersLength === 0}
        variations={variations}
        selectedProgram={calendarProgramFilter}
        onDayClick={onDayClick}
        onEditSession={onEditSession}
        onAddSession={onAddSession}
        onFilterChange={onFilterChange}
        calendarBaseMonth={calendarBaseMonth}
        onCalendarBaseMonthChange={onCalendarBaseMonthChange}
      />
    );
  }

  return (
    <SessionsTable 
      offers={filteredTableOffers}
      isLoading={isLoading}
      onEditOffer={onEditOffer}
      onDeleteOffer={onDeleteOffer}
      onCopyOffer={onCopyOffer}
      onBulkUpdateOffers={onBulkUpdateOffers}
      onDateFilterChange={onDateFilterChange}
      dateFilter={tableDateFilter}
      availabilityStatusOptions={availabilityStatusOptions}
      signupStatusOptions={signupStatusOptions}
      onRefreshOffers={onRefreshOffers}
      isAdmin={isAdmin}
      columnOptions={tableColumnOptions}
    />
  );
};
